import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser } from '../services/apiService';

// Interfaces
export interface User {
  id: string;
  name: string;
  email: string;
  role?: 'user' | 'technician' | 'admin';
  token?: string;
  subscription_status?: 'active' | 'inactive' | 'expired' | 'trial';
  subscription_type?: string;
  is_premium?: boolean;
  trial_ends_at?: string;
  subscription_expires_at?: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  logout: () => void;
  loading: boolean;
}

// Contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verificar usuario al cargar
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('kikai_token');
        const savedUser = localStorage.getItem('kikai_user');
        
        if (savedToken && savedUser) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
        }
      } catch (error) {
        console.error('Error inicializando autenticación:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<User> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, rememberMe })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en login');
      }

      const data = await response.json();
      
      if (!data.token) {
        throw new Error('Login exitoso pero sin token');
      }

      // Guardar usuario y token
      setUser(data.user);
      localStorage.setItem('kikai_user', JSON.stringify(data.user));
      localStorage.setItem('kikai_token', data.token);

      return data.user;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('kikai_user');
    localStorage.removeItem('kikai_token');
  };

  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};